import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import "./accounts_settings_nav";
import "./workflow_nav";
import "./masterdata_nav";
import "./crm_nav";
import "./engineering_nav";
import "./quoting_nav";
import "./sales_nav";
import "./planning_nav";
import "./production_nav";
import "./scheduling_nav";
import "./purchasing_nav";
import "./inventory_nav";
import "./shipping_nav";
import "./quality_nav";
import "./maintenance_nav";
import "./finance_nav";
import "./workforce_nav";
import "./projects_nav";
import "./service_nav";
import "./documents_nav";
import "./reporting_nav";
import "./integrations_nav";
import "./dataops_nav";
import { ApiKeys } from "./accounts_api_keys/ApiKeys";
import { Login } from "./accounts_login_ui/Login";
import { ForgotPassword, ResetPassword, VerifyEmail } from "./accounts_login_ui/PasswordPages";
import { OrganizationSettings } from "./accounts_organization_settings/OrganizationSettings";
import { Roles } from "./accounts_role_editor/Roles";
import { Signup } from "./accounts_signup/Signup";
import { Sites } from "./accounts_site_division/Sites";
import { OnboardingWizard } from "./accounts_tenant_onboarding/OnboardingWizard";
import { Members } from "./accounts_user_directory/Members";
import { AcceptInvitation } from "./accounts_user_invitations/AcceptInvitation";
import { Profile } from "./accounts_user_profiles/Profile";
import { Security } from "./accounts_user_profiles/Security";
import { SsoSettings } from "./accounts_sso/SsoSettings";
import { AuditViewer } from "./audit_audit_viewer/AuditViewer";
import { useAuth } from "./auth/AuthProvider";
import { Billing } from "./billing_billing_portal/Billing";
import { Metrics } from "./console_saas_metrics/Metrics";
import { TenantDetail } from "./console_tenant_console/TenantDetail";
import { Tenants } from "./console_tenant_console/Tenants";
import { LoadingState } from "./components";
import { Home } from "./home/Home";
import { MyActivities } from "./crm_activities/ActivityLog";
import { CustomerDetail } from "./crm_customers/CustomerDetail";
import { Customers } from "./crm_customers/Customers";
import { Opportunities } from "./crm_sales_pipeline/Opportunities";
import { Pipeline } from "./crm_sales_pipeline/Pipeline";
import { Changes } from "./engineering_changes/Changes";
import { ItemDetail } from "./engineering_items/ItemDetail";
import { Items } from "./engineering_items/Items";
import { MarketingLayout } from "./marketing/MarketingLayout";
import { ForecastWorkspace } from "./planning_forecast_workspace/ForecastWorkspace";
import { Mps } from "./planning_mps/Mps";
import { ProductionDashboard } from "./production_dashboard/ProductionDashboard";
import { DispatchList } from "./production_dispatch_list/DispatchList";
import { OutsideProcessing } from "./production_materials/OutsideProcessing";
import { ShopFloorKiosk } from "./production_shop_floor_kiosk/ShopFloorKiosk";
import { WorkOrderDetail } from "./production_work_orders/WorkOrderDetail";
import { WorkOrders } from "./production_work_orders/WorkOrders";
import { BottleneckAnalytics } from "./scheduling_bottleneck_analytics/BottleneckAnalytics";
import { PlanningBoard } from "./scheduling_planning_board/PlanningBoard";
import { MrpWorkbench } from "./planning_mrp_workbench/MrpWorkbench";
import { InventoryDashboard } from "./inventory_dashboard/InventoryDashboard";
import { Counts } from "./inventory_counts/Counts";
import { CountDetail } from "./inventory_counts/CountDetail";
import { LotDetail } from "./inventory_lots/LotDetail";
import { Lots } from "./inventory_lots/Lots";
import { SerialDetail } from "./inventory_lots/SerialDetail";
import { Adjustments } from "./inventory_movements/Adjustments";
import { Transfers } from "./inventory_movements/Transfers";
import { Ledger } from "./inventory_stock_inquiry/Ledger";
import { StockInquiry } from "./inventory_stock_inquiry/StockInquiry";
import { MobileTask } from "./inventory_warehouse_tasks/MobileTask";
import { WarehouseTasks } from "./inventory_warehouse_tasks/WarehouseTasks";
import { ShippingDashboard } from "./shipping_dashboard/ShippingDashboard";
import { PickWaves } from "./shipping_queue/PickWaves";
import { ShippingQueue } from "./shipping_queue/ShippingQueue";
import { ShipmentDetail } from "./shipping_shipments/ShipmentDetail";
import { Shipments } from "./shipping_shipments/Shipments";
import { Audits } from "./quality_audits/Audits";
import { Gauges } from "./quality_calibration/Gauges";
import { CapaDetail } from "./quality_capa/CapaDetail";
import { Capas } from "./quality_capa/Capas";
import { Complaints } from "./quality_complaints/Complaints";
import { QualityDashboard } from "./quality_dashboard/QualityDashboard";
import { QualityReports } from "./quality_documents/Documents";
import { InspectionDetail } from "./quality_inspections/InspectionDetail";
import { Inspections } from "./quality_inspections/Inspections";
import { NcrDetail } from "./quality_ncrs/NcrDetail";
import { Ncrs } from "./quality_ncrs/Ncrs";
import { Plans } from "./quality_plans/Plans";
import { AssetDetail } from "./maintenance_assets/AssetDetail";
import { FinanceDashboard } from "./finance_dashboard/FinanceDashboard";
import { WorkforceDashboard } from "./workforce_dashboard/WorkforceDashboard";
import { DocumentDetail, Library } from "./documents_library/Library";
import { Templates as DocumentTemplates } from "./documents_templates/Templates";
import { LabelDesigner } from "./documents_labels/LabelDesigner";
import { Catalog as ReportCatalog, ReportViewer, RunsAndSchedules } from "./reporting_catalog/Catalog";
import { DashboardDesigner, DashboardView, ExceptionsBoard } from "./reporting_dashboards/Dashboards";
import { CycleTimes } from "./reporting_analytics/CycleTimes";
import { IntegrationCenter } from "./integrations_center/IntegrationCenter";
import { Webhooks } from "./integrations_webhooks/Webhooks";
import { Events as IntegrationEvents } from "./integrations_events/Events";
import { Edi } from "./integrations_edi/Edi";
import { ApiDocsPortal } from "./integrations_api_docs_portal/ApiDocsPortal";
import { ImportHistory, ImportWizard } from "./dataops_imports/ImportWizard";
import { Quality as DataQuality } from "./dataops_quality/Quality";
import { Tenant as TenantData } from "./dataops_tenant/Tenant";
import { JobMonitoringUi } from "./hub_job_monitoring_ui/JobMonitoringUi";
import { DeadLetterOperations } from "./hub_dead_letter_operations/DeadLetterOperations";
import { MaintenanceMode } from "./hub_maintenance_mode/MaintenanceMode";
import { PrintQueue } from "./documents_print_queue/PrintQueue";
import { EmployeeDetail, Employees } from "./workforce_employees/Employees";
import { MyTime, TimeApprovals } from "./workforce_time/TimeApprovals";
import { ProjectDetail, Projects } from "./projects_projects/Projects";
import { ServiceDashboard } from "./service_dispatch/ServiceDashboard";
import { DispatchBoard } from "./service_dispatch/DispatchBoard";
import { ServiceRequests } from "./service_requests/Requests";
import { Equipment as ServiceEquipment, EquipmentDetail as ServiceEquipmentDetail } from "./service_equipment/Equipment";
import { ServiceWorkOrderDetail, ServiceWorkOrders } from "./service_work_orders/WorkOrderDetail";
import { MobileJob, MyJobs } from "./service_mobile/MobileJob";
import { AccountLedger } from "./finance_gl/AccountLedger";
import { Budgets } from "./finance_gl/Budgets";
import { JournalDetail, Journals } from "./finance_gl/Journals";
import { Periods } from "./finance_gl/Periods";
import { Statements } from "./finance_gl/Statements";
import { Collections } from "./finance_ar/Collections";
import { InvoiceDetail } from "./finance_ar/InvoiceDetail";
import { Invoices } from "./finance_ar/Invoices";
import { Receipts } from "./finance_ar/Receipts";
import { PaymentRuns } from "./finance_ap/PaymentRuns";
import { VendorInvoiceDetail } from "./finance_ap/VendorInvoiceDetail";
import { VendorInvoices } from "./finance_ap/VendorInvoices";
import { BankReconciliation } from "./finance_cash/BankReconciliation";
import { Costing } from "./finance_costing/Costing";
import { FixedAssets } from "./finance_costing/FixedAssets";
import { Assets } from "./maintenance_assets/Assets";
import { MaintenanceDispatch } from "./maintenance_maintenance_dispatch/MaintenanceDispatch";
import { Requests as MaintenanceRequests } from "./maintenance_requests/Requests";
import { WorkOrderDetail as MaintenanceWorkOrderDetail } from "./maintenance_work/WorkOrderDetail";
import { BlanketPos } from "./purchasing_blanket_pos/BlanketPos";
import { ExpeditingWorkspace } from "./purchasing_expediting_workspace/ExpeditingWorkspace";
import { Requisitions } from "./purchasing_purchase_requisitions/Requisitions";
import { PurchaseOrderDetail } from "./purchasing_purchase_orders/PurchaseOrderDetail";
import { PurchaseOrders } from "./purchasing_purchase_orders/PurchaseOrders";
import { ReceivingSchedule } from "./purchasing_receiving_schedule/ReceivingSchedule";
import { SupplierRfqs } from "./purchasing_supplier_rfqs/SupplierRfqs";
import { SupplierScorecards } from "./purchasing_supplier_scorecards/SupplierScorecards";
import { Estimator } from "./quoting_estimator/Estimator";
import { QuoteDetail } from "./quoting_quotes/QuoteDetail";
import { Quotes } from "./quoting_quotes/Quotes";
import { RfqDetail } from "./quoting_rfq_workspace/RfqDetail";
import { RfqWorkspace } from "./quoting_rfq_workspace/RfqWorkspace";
import { BlanketOrders } from "./sales_blanket_orders/BlanketOrders";
import { OrderImport } from "./sales_order_import/OrderImport";
import { OrderDetail } from "./sales_orders/OrderDetail";
import { Orders } from "./sales_orders/Orders";
import { RecurringOrders } from "./sales_recurring_orders/RecurringOrders";
import { Rmas } from "./sales_returns/Rmas";
import { CompanyProfile, MasterData } from "./masterdata_master_data/MasterData";
import { ChangelogPage } from "./website_changelog/ChangelogPage";
import { ContactPage } from "./website_demo_requests/ContactPage";
import { DocsPage } from "./website_feature_pages/DocsPage";
import { FeaturesPage, IndustriesPage } from "./website_feature_pages/FeaturePages";
import { LandingPage } from "./website_landing_page/LandingPage";
import { LegalPage } from "./website_legal_pages/LegalPage";
import { PricingPage } from "./website_pricing_page/PricingPage";
import { ApprovalInbox } from "./workflow_approval_inbox/ApprovalInbox";
import { Designer } from "./workflow_designer/Designer";
import { NotificationsPage } from "./workflow_notifications/NotificationCenter";
import { RecurringJobs } from "./workflow_recurring_jobs/RecurringJobs";
import { Reminders } from "./workflow_reminders/Reminders";
import { AppShell } from "./shell/AppShell";

function RequireAuth() {
  const { me, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <LoadingState label="Signing you in…" />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

function RequireStaff() {
  const { me } = useAuth();
  return me?.is_staff ? <Outlet /> : <Navigate to="/app" replace />;
}

function RequirePermission({ permission }: { permission: string }) {
  const { hasPermission } = useAuth();
  return hasPermission(permission) ? <Outlet /> : <Navigate to="/app" replace />;
}

export function App() {
  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/industries" element={<IndustriesPage />} />
        <Route path="/industries/:slug" element={<IndustriesPage />} />
        <Route path="/docs/*" element={<DocsPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/contact" element={<ContactPage kind="contact" />} />
        <Route path="/demo" element={<ContactPage kind="demo" />} />
        {["terms", "privacy", "dpa", "security", "cookies"].map((k) => <Route key={k} path={`/${k}`} element={<LegalPage kind={k} />} />)}
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
      <Route path="/verify-email/:uid/:token" element={<VerifyEmail />} />
      <Route path="/invitations/:token" element={<AcceptInvitation />} />
      <Route element={<RequireAuth />}>
        <Route path="/app" element={<AppShell />}>
          <Route index element={<Home />} />
          <Route path="onboarding" element={<OnboardingWizard />} />
          <Route path="approvals" element={<ApprovalInbox />} />
          <Route path="masterdata/company" element={<CompanyProfile />} />
          <Route element={<RequirePermission permission="crm.customer.view" />}>
            <Route path="crm/customers" element={<Customers />} />
            <Route path="crm/customers/:id" element={<CustomerDetail />} />
          </Route>
          <Route element={<RequirePermission permission="crm.opportunity.view" />}>
            <Route path="crm/pipeline" element={<Pipeline />} />
            <Route path="crm/opportunities" element={<Opportunities />} />
          </Route>
          <Route path="crm/activities" element={<MyActivities />} />
          <Route element={<RequirePermission permission="engineering.item.view" />}>
            <Route path="engineering/items" element={<Items />} />
            <Route path="engineering/items/:id" element={<ItemDetail />} />
          </Route>
          <Route element={<RequirePermission permission="engineering.change.view" />}>
            <Route path="engineering/changes" element={<Changes />} />
          </Route>
          <Route element={<RequirePermission permission="quoting.rfq.view" />}>
            <Route path="quoting/rfqs" element={<RfqWorkspace />} />
            <Route path="quoting/rfqs/:id" element={<RfqDetail />} />
          </Route>
          <Route element={<RequirePermission permission="quoting.quote.view" />}>
            <Route path="quoting/quotes" element={<Quotes />} />
            <Route path="quoting/quotes/:id" element={<QuoteDetail />} />
          </Route>
          <Route element={<RequirePermission permission="quoting.estimating.view" />}>
            <Route path="quoting/estimator" element={<Estimator />} />
          </Route>
          <Route element={<RequirePermission permission="sales.order.view" />}>
            <Route path="sales/orders" element={<Orders />} />
            <Route path="sales/orders/:id" element={<OrderDetail />} />
          </Route>
          <Route element={<RequirePermission permission="sales.blanket.view" />}>
            <Route path="sales/blanket-orders" element={<BlanketOrders />} />
          </Route>
          <Route element={<RequirePermission permission="sales.recurring.view" />}>
            <Route path="sales/recurring-orders" element={<RecurringOrders />} />
          </Route>
          <Route element={<RequirePermission permission="sales.rma.view" />}>
            <Route path="sales/rmas" element={<Rmas />} />
          </Route>
          <Route element={<RequirePermission permission="sales.import.run" />}>
            <Route path="sales/imports" element={<OrderImport />} />
          </Route>
          <Route element={<RequirePermission permission="planning.mrp.view" />}>
            <Route path="planning/mrp" element={<MrpWorkbench />} />
          </Route>
          <Route element={<RequirePermission permission="planning.forecast.view" />}>
            <Route path="planning/forecasts" element={<ForecastWorkspace />} />
          </Route>
          <Route element={<RequirePermission permission="planning.mps.view" />}>
            <Route path="planning/mps" element={<Mps />} />
          </Route>
          <Route element={<RequirePermission permission="production.work_order.view" />}>
            <Route path="production/dashboard" element={<ProductionDashboard />} />
            <Route path="production/work-orders" element={<WorkOrders />} />
            <Route path="production/work-orders/:id" element={<WorkOrderDetail />} />
          </Route>
          <Route element={<RequirePermission permission="production.execution.view" />}>
            <Route path="production/dispatch" element={<DispatchList />} />
            <Route path="production/kiosk" element={<ShopFloorKiosk />} />
          </Route>
          <Route element={<RequirePermission permission="production.material.view" />}>
            <Route path="production/outside" element={<OutsideProcessing />} />
          </Route>
          <Route element={<RequirePermission permission="scheduling.schedule.view" />}>
            <Route path="scheduling/board" element={<PlanningBoard />} />
            <Route path="scheduling/bottlenecks" element={<BottleneckAnalytics />} />
          </Route>
          <Route element={<RequirePermission permission="purchasing.order.view" />}>
            <Route path="purchasing/orders" element={<PurchaseOrders />} />
            <Route path="purchasing/orders/:id" element={<PurchaseOrderDetail />} />
            <Route path="purchasing/receiving" element={<ReceivingSchedule />} />
            <Route path="purchasing/expediting" element={<ExpeditingWorkspace />} />
          </Route>
          <Route element={<RequirePermission permission="purchasing.requisition.view" />}>
            <Route path="purchasing/requisitions" element={<Requisitions />} />
          </Route>
          <Route element={<RequirePermission permission="purchasing.rfq.view" />}>
            <Route path="purchasing/rfqs" element={<SupplierRfqs />} />
          </Route>
          <Route element={<RequirePermission permission="purchasing.blanket.view" />}>
            <Route path="purchasing/blanket-pos" element={<BlanketPos />} />
          </Route>
          <Route element={<RequirePermission permission="purchasing.analytics.view" />}>
            <Route path="purchasing/scorecards" element={<SupplierScorecards />} />
          </Route>
          <Route element={<RequirePermission permission="inventory.stock.view" />}>
            <Route path="inventory" element={<InventoryDashboard />} />
            <Route path="inventory/stock" element={<StockInquiry />} />
            <Route path="inventory/ledger" element={<Ledger />} />
            <Route path="inventory/lots" element={<Lots />} />
            <Route path="inventory/lots/:id" element={<LotDetail />} />
            <Route path="inventory/serials/:id" element={<SerialDetail />} />
          </Route>
          <Route element={<RequirePermission permission="inventory.task.view" />}>
            <Route path="inventory/tasks" element={<WarehouseTasks />} />
            <Route path="inventory/tasks/:id" element={<MobileTask />} />
            <Route path="inventory/mobile" element={<MobileTask />} />
          </Route>
          <Route element={<RequirePermission permission="inventory.count.view" />}>
            <Route path="inventory/counts" element={<Counts />} />
            <Route path="inventory/counts/:id" element={<CountDetail />} />
          </Route>
          <Route element={<RequirePermission permission="inventory.movement.view" />}>
            <Route path="inventory/transfers" element={<Transfers />} />
            <Route path="inventory/adjustments" element={<Adjustments />} />
          </Route>
          <Route element={<RequirePermission permission="shipping.shipment.view" />}>
            <Route path="shipping" element={<ShippingDashboard />} />
            <Route path="shipping/queue" element={<ShippingQueue />} />
            <Route path="shipping/shipments" element={<Shipments />} />
            <Route path="shipping/shipments/:id" element={<ShipmentDetail />} />
            <Route path="shipping/waves" element={<PickWaves />} />
          </Route>
          <Route element={<RequirePermission permission="quality.inspection.view" />}>
            <Route path="quality" element={<QualityDashboard />} />
            <Route path="quality/inspections" element={<Inspections />} />
            <Route path="quality/inspections/:id" element={<InspectionDetail />} />
          </Route>
          <Route element={<RequirePermission permission="quality.ncr.view" />}>
            <Route path="quality/ncrs" element={<Ncrs />} />
            <Route path="quality/ncrs/:id" element={<NcrDetail />} />
            <Route path="quality/complaints" element={<Complaints />} />
          </Route>
          <Route element={<RequirePermission permission="quality.capa.view" />}>
            <Route path="quality/capas" element={<Capas />} />
            <Route path="quality/capas/:id" element={<CapaDetail />} />
          </Route>
          <Route element={<RequirePermission permission="quality.audit.view" />}>
            <Route path="quality/audits" element={<Audits />} />
          </Route>
          <Route element={<RequirePermission permission="quality.calibration.view" />}>
            <Route path="quality/gauges" element={<Gauges />} />
          </Route>
          <Route element={<RequirePermission permission="quality.document.view" />}>
            <Route path="quality/reports" element={<QualityReports />} />
          </Route>
          <Route element={<RequirePermission permission="quality.config.view" />}>
            <Route path="quality/plans" element={<Plans />} />
          </Route>
          <Route element={<RequirePermission permission="maintenance.work.view" />}>
            <Route path="maintenance" element={<MaintenanceDispatch />} />
            <Route path="maintenance/work/:id" element={<MaintenanceWorkOrderDetail />} />
            <Route path="maintenance/requests" element={<MaintenanceRequests />} />
          </Route>
          <Route element={<RequirePermission permission="maintenance.asset.view" />}>
            <Route path="maintenance/assets" element={<Assets />} />
            <Route path="maintenance/assets/:id" element={<AssetDetail />} />
          </Route>
          <Route element={<RequirePermission permission="finance.gl.view" />}>
            <Route path="finance" element={<FinanceDashboard />} />
            <Route path="finance/journals" element={<Journals />} />
            <Route path="finance/journals/:id" element={<JournalDetail />} />
            <Route path="finance/accounts/:id" element={<AccountLedger />} />
            <Route path="finance/statements" element={<Statements />} />
            <Route path="finance/periods" element={<Periods />} />
            <Route path="finance/budgets" element={<Budgets />} />
          </Route>
          <Route element={<RequirePermission permission="finance.ar.view" />}>
            <Route path="finance/invoices" element={<Invoices />} />
            <Route path="finance/invoices/:id" element={<InvoiceDetail />} />
            <Route path="finance/receipts" element={<Receipts />} />
            <Route path="finance/collections" element={<Collections />} />
          </Route>
          <Route element={<RequirePermission permission="finance.ap.view" />}>
            <Route path="finance/vendor-invoices" element={<VendorInvoices />} />
            <Route path="finance/vendor-invoices/:id" element={<VendorInvoiceDetail />} />
            <Route path="finance/payment-runs" element={<PaymentRuns />} />
          </Route>
          <Route element={<RequirePermission permission="finance.cash.view" />}>
            <Route path="finance/bank" element={<BankReconciliation />} />
          </Route>
          <Route element={<RequirePermission permission="finance.costing.view" />}>
            <Route path="finance/costing" element={<Costing />} />
            <Route path="finance/fixed-assets" element={<FixedAssets />} />
          </Route>
          <Route path="workforce" element={<WorkforceDashboard />} />
          <Route path="workforce/me" element={<MyTime />} />
          <Route element={<RequirePermission permission="workforce.time.view" />}>
            <Route path="workforce/time" element={<TimeApprovals />} />
          </Route>
          <Route element={<RequirePermission permission="workforce.employee.view" />}>
            <Route path="workforce/employees" element={<Employees />} />
            <Route path="workforce/employees/:id" element={<EmployeeDetail />} />
          </Route>
          <Route element={<RequirePermission permission="projects.project.view" />}>
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
          </Route>
          <Route element={<RequirePermission permission="service.work.view" />}>
            <Route path="service" element={<ServiceDashboard />} />
            <Route path="service/dispatch" element={<DispatchBoard />} />
            <Route path="service/work" element={<ServiceWorkOrders />} />
            <Route path="service/work/:id" element={<ServiceWorkOrderDetail />} />
          </Route>
          <Route path="service/mobile" element={<MyJobs />} />
          <Route path="service/mobile/:id" element={<MobileJob />} />
          <Route element={<RequirePermission permission="service.request.view" />}>
            <Route path="service/requests" element={<ServiceRequests />} />
          </Route>
          <Route element={<RequirePermission permission="service.equipment.view" />}>
            <Route path="service/equipment" element={<ServiceEquipment />} />
            <Route path="service/equipment/:id" element={<ServiceEquipmentDetail />} />
          </Route>
          <Route element={<RequirePermission permission="documents.library.view" />}>
            <Route path="documents" element={<Library />} />
            <Route path="documents/templates" element={<DocumentTemplates />} />
            <Route path="documents/labels" element={<LabelDesigner />} />
            <Route path="documents/:id" element={<DocumentDetail />} />
          </Route>
          <Route element={<RequirePermission permission="reporting.report.view" />}>
            <Route path="reporting" element={<ReportCatalog />} />
            <Route path="reporting/reports/:key" element={<ReportViewer />} />
            <Route path="reporting/runs" element={<RunsAndSchedules />} />
          </Route>
          <Route element={<RequirePermission permission="reporting.dashboard.view" />}>
            <Route path="reporting/dashboards" element={<DashboardView />} />
            <Route path="reporting/dashboards/:id" element={<DashboardView />} />
            <Route path="reporting/dashboards/:id/design" element={<DashboardDesigner />} />
            <Route path="reporting/exceptions" element={<ExceptionsBoard />} />
          </Route>
          <Route element={<RequirePermission permission="reporting.analytics.view" />}>
            <Route path="reporting/cycle-times" element={<CycleTimes />} />
          </Route>
          <Route element={<RequirePermission permission="integrations.connection.view" />}>
            <Route path="integrations" element={<IntegrationCenter />} />
          </Route>
          <Route element={<RequirePermission permission="integrations.webhook.view" />}>
            <Route path="integrations/webhooks" element={<Webhooks />} />
          </Route>
          <Route element={<RequirePermission permission="integrations.event.view" />}>
            <Route path="integrations/events" element={<IntegrationEvents />} />
          </Route>
          <Route element={<RequirePermission permission="integrations.edi.view" />}>
            <Route path="integrations/edi" element={<Edi />} />
          </Route>
          <Route path="integrations/developers" element={<ApiDocsPortal />} />
          <Route element={<RequirePermission permission="dataops.import.view" />}>
            <Route path="dataops/imports" element={<ImportWizard />} />
            <Route path="dataops/imports/history" element={<ImportHistory />} />
            <Route path="dataops/imports/history/:id" element={<ImportHistory />} />
          </Route>
          <Route element={<RequirePermission permission="dataops.quality.view" />}>
            <Route path="dataops/quality" element={<DataQuality />} />
          </Route>
          <Route element={<RequirePermission permission="dataops.tenant.export" />}>
            <Route path="dataops/tenant" element={<TenantData />} />
          </Route>
          <Route path="ops/jobs" element={<JobMonitoringUi />} />
          <Route path="ops/jobs/:id" element={<JobMonitoringUi />} />
          <Route path="ops/dead-letters" element={<DeadLetterOperations />} />
          <Route path="ops/maintenance" element={<MaintenanceMode />} />
          <Route element={<RequirePermission permission="documents.print.view" />}>
            <Route path="documents/print-queue" element={<PrintQueue />} />
          </Route>
          <Route path="masterdata/:key" element={<MasterData />} />
          <Route path="reminders" element={<Reminders />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route element={<RequirePermission permission="workflow.automation.view" />}>
            <Route path="settings/automation" element={<Designer />} />
            <Route path="settings/jobs" element={<RecurringJobs />} />
          </Route>
          <Route path="settings/profile" element={<Profile />} />
          <Route path="settings/security" element={<Security />} />
          <Route path="settings/api-keys" element={<ApiKeys />} />
          <Route element={<RequirePermission permission="accounts.organization.view" />}>
            <Route path="settings/organization" element={<OrganizationSettings />} />
          </Route>
          <Route element={<RequirePermission permission="accounts.site.view" />}>
            <Route path="settings/sites" element={<Sites />} />
          </Route>
          <Route element={<RequirePermission permission="accounts.membership.view" />}>
            <Route path="settings/members" element={<Members />} />
          </Route>
          <Route element={<RequirePermission permission="accounts.role.view" />}>
            <Route path="settings/roles" element={<Roles />} />
          </Route>
          <Route element={<RequirePermission permission="billing.subscription.view" />}>
            <Route path="settings/billing" element={<Billing />} />
          </Route>
          <Route element={<RequirePermission permission="accounts.sso.view" />}>
            <Route path="settings/sso" element={<SsoSettings />} />
          </Route>
          <Route element={<RequirePermission permission="audit.log.view" />}>
            <Route path="settings/audit" element={<AuditViewer />} />
          </Route>
        </Route>
        <Route element={<RequireStaff />}>
          <Route path="/console" element={<AppShell />}>
            <Route index element={<Tenants />} />
            <Route path="organizations/:id" element={<TenantDetail />} />
            <Route path="metrics" element={<Metrics />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
